/**
 * TravelTimeControls.jsx — explicit Start/Stop Travel buttons on a job
 * (Feb 2026). Shows the current open segment (if any) and lets the
 * worker close it or open a new one (travel_out, travel_return,
 * parts_pickup).
 *
 * The outside-geofence override on /start automatically opens a
 * `travel_out` segment without any explicit user interaction — this
 * component is for the manual cases:
 *   • Worker taps "Start Travel" before getting in the truck.
 *   • Worker taps "Heading back" after wrapping up to log the return trip.
 *   • Worker taps "Parts run" mid-job (rare — usually a Pause + Resume).
 *
 * Network contract:
 *   POST /api/jobs/{id}/travel/start   { latitude, longitude, kind }
 *   POST /api/jobs/{id}/travel/stop    { latitude, longitude }
 *   GET  /api/jobs/{id}/travel         → { segments, total_minutes,
 *                                          open_segment_id }
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Truck, Square, MapPin, Loader2, Clock, Coffee, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import QrScanDialog from './QrScanDialog';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const KIND_LABELS = {
  travel_out: 'To site',
  travel_return: 'Back to shop',
  parts_pickup: 'Parts run',
  break: 'On break',
};

export default function TravelTimeControls({ jobId, siteQrRequired, onChange }) {
  const [data, setData] = useState({ segments: [], total_minutes: 0, open_segment_id: null });
  const [loading, setLoading] = useState(true);
  const [busyKind, setBusyKind] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/jobs/${jobId}/travel`, { headers: authHeaders() });
      setData(r.data || { segments: [], total_minutes: 0, open_segment_id: null });
    } catch {
      /* silent — keeps stale data on transient failures */
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const openSegment = useMemo(
    () => data.segments?.find((s) => !s.ended_at) || null,
    [data.segments],
  );

  const captureGps = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ lat: null, lon: null });
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
        () => resolve({ lat: null, lon: null }),
        { timeout: 8000 },
      );
    });

  const startTravel = async (kind) => {
    if (openSegment) {
      toast.error('Stop your current travel segment first.');
      return;
    }
    setBusyKind(kind);
    const { lat, lon } = await captureGps();
    try {
      await axios.post(
        `${API}/jobs/${jobId}/travel/start`,
        { latitude: lat ?? 0, longitude: lon ?? 0, kind },
        { headers: authHeaders() },
      );
      toast.success(`Travel Time started — ${KIND_LABELS[kind]}`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start travel');
    } finally {
      setBusyKind(null);
    }
  };

  const stopTravel = async (qrToken = null) => {
    setBusyKind('stop');
    const { lat, lon } = await captureGps();
    try {
      const r = await axios.post(
        `${API}/jobs/${jobId}/travel/stop`,
        { latitude: lat ?? 0, longitude: lon ?? 0, ...(qrToken ? { site_qr_token: qrToken } : {}) },
        { headers: authHeaders() },
      );
      const mins = Math.round(r.data?.duration_minutes || 0);
      toast.success(qrToken
        ? `Back on site (QR verified) — ${mins} min logged`
        : `Travel stopped — ${mins} min logged`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not stop travel');
    } finally {
      setBusyKind(null);
    }
  };

  // Ending a break on a QR-enabled job → ask the worker to scan the site
  // poster (proof they're back on site). Skipping still ends the break.
  const handleStop = () => {
    if (openSegment?.kind === 'break' && siteQrRequired) {
      setScanOpen(true);
      return;
    }
    stopTravel();
  };

  if (loading && data.segments.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="travel-controls-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading travel time…
      </div>
    );
  }

  const totalH = (data.total_minutes / 60).toFixed(2);

  return (
    <div className="space-y-3" data-testid="travel-time-controls">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-amber-400" />
          <span className="text-slate-200 font-medium">Travel Time</span>
          <span className="text-slate-400">
            · {totalH}h logged across {data.segments.length} segment{data.segments.length === 1 ? '' : 's'}
          </span>
        </div>
        {openSegment ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200"
            data-testid="travel-open-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            {openSegment.kind === 'break' ? 'On break' : `On ${KIND_LABELS[openSegment.kind] || openSegment.kind}`}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {openSegment ? (
          <Button
            size="sm"
            onClick={handleStop}
            disabled={busyKind != null}
            className={openSegment.kind === 'break'
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-rose-600 hover:bg-rose-700 text-white'}
            data-testid="travel-stop-btn"
          >
            {busyKind === 'stop'
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : openSegment.kind === 'break' && siteQrRequired
                ? <QrCode className="w-3.5 h-3.5 mr-1.5" />
                : <Square className="w-3.5 h-3.5 mr-1.5" />}
            {openSegment.kind === 'break'
              ? (siteQrRequired ? 'Back to work — Scan Site QR' : 'Back to work')
              : 'Stop Travel'}
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startTravel('travel_out')}
              disabled={busyKind != null}
              className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
              data-testid="travel-start-out-btn"
            >
              {busyKind === 'travel_out' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Truck className="w-3.5 h-3.5 mr-1.5" />}
              Heading to site
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startTravel('travel_return')}
              disabled={busyKind != null}
              className="border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
              data-testid="travel-start-return-btn"
            >
              {busyKind === 'travel_return' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Truck className="w-3.5 h-3.5 mr-1.5" />}
              Heading back to shop
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startTravel('parts_pickup')}
              disabled={busyKind != null}
              className="border-violet-500/50 text-violet-300 hover:bg-violet-500/10"
              data-testid="travel-start-parts-btn"
            >
              {busyKind === 'parts_pickup' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5 mr-1.5" />}
              Parts run
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startTravel('break')}
              disabled={busyKind != null}
              className="border-teal-500/50 text-teal-300 hover:bg-teal-500/10"
              data-testid="travel-start-break-btn"
            >
              {busyKind === 'break' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Coffee className="w-3.5 h-3.5 mr-1.5" />}
              Taking a break
            </Button>
          </>
        )}
      </div>

      {data.segments.length > 0 && (
        <div className="rounded-md border border-slate-700 bg-slate-900/40 divide-y divide-slate-800" data-testid="travel-segments-list">
          {data.segments.map((s) => (
            <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-slate-200 whitespace-nowrap">
                  {KIND_LABELS[s.kind] || s.kind}
                </span>
                {s.reason ? (
                  <span className="text-slate-500 truncate">
                    · {s.reason.replace(/_/g, ' ')}{s.notes ? ` — ${s.notes}` : ''}
                  </span>
                ) : null}
              </div>
              <div className="text-slate-400 whitespace-nowrap">
                {s.ended_at
                  ? `${Math.round(s.duration_minutes || 0)} min`
                  : <span className="text-amber-300">Open</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Break resume — scan the site poster to prove you're back on site. */}
      <QrScanDialog
        open={scanOpen}
        title="Scan to end your break"
        description="Scan the QR code posted at the job site to confirm you're back on site."
        skipLabel="Can't scan? End break without scanning"
        onScan={(tok) => { setScanOpen(false); stopTravel(tok); }}
        onSkip={() => { setScanOpen(false); stopTravel(); }}
        onClose={() => setScanOpen(false)}
      />
    </div>
  );
}
